import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getMyAccount } from '../lib/account';
import { isDemoMode } from '../lib/demo/harness';

/**
 * Web-app access gate (CC-SPEC item 4). The product is the MCP connector,
 * not the web chat app, so the chat/document routes are owner-only for now.
 * This provider resolves the authenticated user's `app_access` flag (from
 * `get_my_account`) once per session and shares it with the route guard.
 *
 * Fails CLOSED: on error or no user, access is denied (non-owners must not
 * slip into the app on a transient failure). The single owner can reload if
 * a fetch was transient.
 */
interface AppAccessState {
  appAccess: boolean | null; // null = not yet resolved
  loading: boolean;
}

const AppAccessContext = createContext<AppAccessState>({ appAccess: null, loading: true });

export function useAppAccess() {
  return useContext(AppAccessContext);
}

export function AppAccessProvider({ children }: { children: ReactNode }) {
  if (isDemoMode()) {
    return (
      <AppAccessContext.Provider value={{ appAccess: true, loading: false }}>
        {children}
      </AppAccessContext.Provider>
    );
  }
  return <LiveAppAccessProvider>{children}</LiveAppAccessProvider>;
}

function LiveAppAccessProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  // `app_access` fetched for the *current* user; null while unresolved.
  // The public { appAccess, loading } shape is derived from this below.
  const [fetched, setFetched] = useState<boolean | null>(null);

  // When the authenticated user changes, drop the previous resolution
  // immediately (render-phase adjustment per react.dev "adjusting state
  // when a prop changes") so one user's flag is never served while
  // another's fetch is in flight — the gate keeps failing closed across
  // user switches.
  const [prevUser, setPrevUser] = useState(user);
  if (prevUser !== user) {
    setPrevUser(user);
    setFetched(null);
  }

  useEffect(() => {
    if (authLoading || !user) return; // wait for auth to settle

    let cancelled = false;
    getMyAccount()
      .then((account) => {
        if (!cancelled) setFetched(account.app_access === true);
      })
      .catch(() => {
        if (!cancelled) setFetched(false); // fail closed
      });

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  // No user → denied (fail closed); user → the fetched flag once resolved,
  // loading until then.
  const appAccess = authLoading ? null : user ? fetched : false;
  const loading = authLoading ? true : user ? fetched === null : false;

  return (
    <AppAccessContext.Provider value={{ appAccess, loading }}>
      {children}
    </AppAccessContext.Provider>
  );
}
